import { Lock01 } from '@untitledui/icons';
import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

/**
 * The muted stand-in for a chat composer on a seat that lacks the Chat
 * feature. One implementation for every surface that has a composer
 * (dashboard omnibox, SeatGatedChat, the full-page record) so they cannot
 * drift apart.
 *
 * Styled with the disabled design tokens but NOT the native `disabled`
 * attribute — that would swallow clicks. Activating it hands the user to
 * `onActivate`, which each surface points at its upgrade path (the chat
 * paywall route, or scrolling the upgrade card into view).
 */
export interface LockedComposerProps {
  /**
   * Visible placeholder-style text inside the facade. Defaults to the one
   * label every surface shares — override only when a surface has a reason
   * to say something more specific.
   */
  label?: string;
  onActivate: () => void;
}

export const LOCKED_COMPOSER_LABEL = 'Request a full seat to use Chat';
export const LOCKED_COMPOSER_ARIA_LABEL =
  'Chat unavailable – request a full seat to use Chat';

export function LockedComposer({
  label = LOCKED_COMPOSER_LABEL,
  onActivate,
}: LockedComposerProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
    [onActivate]
  );

  return (
    <div
      role='button'
      tabIndex={0}
      aria-disabled='true'
      aria-label={LOCKED_COMPOSER_ARIA_LABEL}
      className='flex cursor-default select-none items-center gap-3 rounded-xl border border-[var(--color-border-disabled_subtle)] bg-[var(--color-bg-disabled_subtle)] px-4 py-3'
      onClick={onActivate}
      onKeyDown={handleKeyDown}
    >
      <span className='flex-1 text-sm text-[var(--color-text-disabled)]'>
        {label}
      </span>
      <Lock01
        className='size-4 shrink-0 text-[var(--color-fg-disabled)]'
        aria-hidden='true'
      />
    </div>
  );
}
